/**
 * Moteur de lecture et de mixage de la timeline multipiste.
 *
 * Chaque piste a un volume + mute/solo ; chaque clip est un morceau d'AudioBuffer
 * (offset/durée dans le buffer) posé à une position `start` sur la timeline.
 * La lecture planifie toutes les sources au bon instant via Web Audio, à travers
 * un gain par piste puis un bus master (gain de compensation + limiteur).
 */
import { getContext, unlock } from './context';
import { toMono, encodeWav } from './wav';

export interface EngineClip {
  buffer: AudioBuffer;
  start: number;   // position sur la timeline (s)
  offset: number;  // début dans le buffer (s)
  duration: number; // durée jouée (s)
}

export interface EngineTrack {
  volume: number;
  muted: boolean;
  solo: boolean;
  clips: EngineClip[];
}

function audible(tracks: EngineTrack[]): (t: EngineTrack) => boolean {
  const anySolo = tracks.some((t) => t.solo);
  return (t) => !t.muted && (!anySolo || t.solo);
}

export class TimelineEngine {
  private sources: AudioBufferSourceNode[] = [];
  private trackGains: GainNode[] = [];
  private gain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private startCtx = 0;
  private startPos = 0;
  private playing = false;
  private endTimer: number | null = null;
  private seekTimer: number | null = null;
  private playSeq = 0; // invalide les play() en attente d'unlock après un stop
  private lastTracks: EngineTrack[] = [];
  onChange?: () => void;
  onEnded?: () => void;

  private master(): GainNode {
    const ctx = getContext();
    if (!this.gain) {
      this.gain = ctx.createGain();
      this.gain.gain.value = 1.4; // makeup (Web Audio faible sur mobile)
      this.limiter = ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -3;
      this.limiter.knee.value = 0;
      this.limiter.ratio.value = 20;
      this.limiter.attack.value = 0.003;
      this.limiter.release.value = 0.25;
      this.gain.connect(this.limiter);
      this.limiter.connect(ctx.destination);
    }
    return this.gain;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get time(): number {
    if (!this.playing) return this.startPos;
    // clampe le petit pré-roll (60 ms) pour ne jamais afficher t < position de départ
    return Math.max(this.startPos, this.startPos + (getContext().currentTime - this.startCtx));
  }

  async play(tracks: EngineTrack[], from: number): Promise<void> {
    const seq = ++this.playSeq;
    await unlock();
    if (seq !== this.playSeq) return; // stop()/play() plus récent pendant l'attente
    this.stop(true);
    this.lastTracks = tracks;
    const ctx = getContext();
    const t0 = ctx.currentTime + 0.06;
    const isAudible = audible(tracks);
    let lastEnd = 0;

    for (const track of tracks) {
      if (!isAudible(track)) continue;
      const g = ctx.createGain();
      g.gain.value = Math.max(0, Math.min(1, track.volume));
      g.connect(this.master());
      this.trackGains.push(g);
      for (const c of track.clips) {
        const clipEnd = c.start + c.duration;
        if (clipEnd <= from) continue;
        const when = t0 + Math.max(0, c.start - from);
        const into = c.offset + Math.max(0, from - c.start);
        const dur = clipEnd - Math.max(c.start, from);
        if (dur <= 0.005) continue;
        const src = ctx.createBufferSource();
        src.buffer = c.buffer;
        src.connect(g);
        try {
          src.start(when, into, dur);
        } catch {
          continue;
        }
        this.sources.push(src);
        if (clipEnd > lastEnd) lastEnd = clipEnd;
      }
    }

    // Rien à jouer (position au-delà de la fin, pistes vides…) : on ne
    // démarre pas une lecture fantôme qui ne s'arrêterait jamais.
    if (!this.sources.length) {
      this.startPos = from;
      this.playing = false;
      this.onChange?.();
      return;
    }

    this.startCtx = t0;
    this.startPos = from;
    this.playing = true;
    this.onChange?.();

    // Arrêt automatique à la fin du dernier clip (annulé par tout stop()).
    const ms = (lastEnd - from) * 1000 + 120;
    this.endTimer = window.setTimeout(() => {
      this.endTimer = null;
      if (this.playing) {
        this.stop();
        this.onEnded?.();
      }
    }, ms);
  }

  stop(silent = false): void {
    this.playSeq++; // annule tout play() encore en attente d'unlock
    const had = this.playing || this.sources.length > 0;
    if (this.endTimer != null) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
    if (this.seekTimer != null) {
      clearTimeout(this.seekTimer);
      this.seekTimer = null;
    }
    const pos = this.playing ? this.time : this.startPos; // conserve la position réelle
    const ctx = getContext();
    const now = ctx.currentTime;
    const sources = this.sources;
    const gains = this.trackGains;
    this.sources = [];
    this.trackGains = [];
    // petit fondu (~10 ms) pour éviter le clic, puis libération des nœuds
    for (const g of gains) {
      try {
        g.gain.setTargetAtTime(0, now, 0.004);
      } catch {
        /* contexte fermé */
      }
    }
    for (const s of sources) {
      try {
        s.onended = null;
        s.stop(now + 0.03);
      } catch {
        /* déjà arrêtée */
      }
    }
    window.setTimeout(() => {
      for (const s of sources) { try { s.disconnect(); } catch { /* ok */ } }
      for (const g of gains) { try { g.disconnect(); } catch { /* ok */ } }
    }, 100);
    this.playing = false;
    this.startPos = Math.max(0, pos);
    if (had && !silent) this.onChange?.();
  }

  seek(pos: number, tracks?: EngineTrack[]): void {
    const p = Math.max(0, pos);
    if (tracks) this.lastTracks = tracks; // état frais fourni par l'appelant
    if (this.playing) {
      // l'affichage suit immédiatement le scrub…
      this.startPos = p;
      this.startCtx = getContext().currentTime;
      // …et la re-planification audio est débouncée (un drag de la règle
      // envoie des dizaines de seeks : on ne replanifie qu'à l'accalmie)
      if (this.seekTimer != null) clearTimeout(this.seekTimer);
      this.seekTimer = window.setTimeout(() => {
        this.seekTimer = null;
        if (this.playing) void this.play(this.lastTracks, this.time);
      }, 90);
    } else {
      this.startPos = p;
      this.onChange?.();
    }
  }

  /** Libère le bus master (à l'unmount du studio). */
  dispose(): void {
    this.stop(true);
    try { this.gain?.disconnect(); } catch { /* ok */ }
    try { this.limiter?.disconnect(); } catch { /* ok */ }
    this.gain = null;
    this.limiter = null;
  }
}

/** Longueur totale de l'arrangement (fin du dernier clip), en secondes. */
export function projectDuration(tracks: EngineTrack[]): number {
  let end = 0;
  for (const t of tracks) for (const c of t.clips) end = Math.max(end, c.start + c.duration);
  return end;
}

/** Mixe l'arrangement (ou un sous-ensemble de pistes) en mono, avec volume/mute/solo. */
export function mixdown(tracks: EngineTrack[], sampleRate: number, only?: Set<number>): Float32Array {
  // mute/solo évalués sur le sous-ensemble sélectionné, pas sur l'ensemble
  // (sinon un solo hors sélection rendrait la sélection silencieuse)
  const sel = only ? tracks.filter((_, i) => only.has(i)) : tracks;
  const isAudible = audible(sel);
  let endSec = 0;
  for (const t of sel) {
    if (!isAudible(t)) continue; // une piste muette ne doit pas allonger le rendu
    for (const c of t.clips) endSec = Math.max(endSec, c.start + c.duration);
  }
  const len = Math.max(1, Math.ceil(endSec * sampleRate));
  const out = new Float32Array(len);

  for (const t of sel) {
    if (!isAudible(t)) continue;
    const v = Math.max(0, Math.min(1, t.volume));
    for (const c of t.clips) {
      const mono = toMono(c.buffer, c.offset, c.offset + c.duration);
      const at = Math.floor(c.start * sampleRate);
      for (let j = 0; j < mono.length; j++) {
        const k = at + j;
        if (k >= 0 && k < len) out[k] += mono[j] * v;
      }
    }
  }

  let peak = 0;
  for (let i = 0; i < len; i++) {
    const a = Math.abs(out[i]);
    if (a > peak) peak = a;
  }
  if (peak > 1) for (let i = 0; i < len; i++) out[i] /= peak;
  return out;
}

export function mixdownToWav(tracks: EngineTrack[], sampleRate: number, only?: Set<number>): Blob {
  return encodeWav(mixdown(tracks, sampleRate, only), sampleRate);
}
